import { t } from '@i18n';
import { country_enum, envelope_type_enum } from '@predium/enums';
import { isNil } from 'lodash';
import { FieldErrors, FieldNamesMarkedBoolean } from 'react-hook-form';
import * as yup from 'yup';

/**
 * Calculate the total count of fields or errors.
 *
 * @param fields - The fields or errors object.
 * @returns The total count.
 */
export function calculateTotalCount(
  fields: FieldErrors<any> | FieldNamesMarkedBoolean<any>,
  isErrors?: boolean,
  ignoreFields?: string[],
): number;
export function calculateTotalCount(
  fields: FieldErrors<Record<string, any[]>> | FieldNamesMarkedBoolean<Record<string, any[]>>,
  isErrors: boolean = false,
  ignoreFields: string[] = [],
): number {
  return Object.values(fields).reduce((count: number, array: any[]) => {
    array.forEach((object) => {
      for (const key in object) {
        if (object.hasOwnProperty(key) && (isErrors || object[key] === true) && !ignoreFields.includes(key)) {
          count++;
        }
      }
    });
    return count;
  }, 0);
}

//extraFields which are not show in the UI. so will never be edited. they only used for UI manipulations and
//will be removed later from the code during the updating envelopes UI with new designs
let extraFields = [
  'envelope_id',
  'data_source_type_id',
  'insulation_material_custom',
  'had_insulation',
  'has_insulation',
  'id',
  'delete',
  'create',
  'envelope_type',
  'u_value',
];

/**
 * Calculate the total count of envelope edited fields
 *
 * @param dirtyFields - The dirty fields object.
 * @param values - The current form data object.
 * @returns The total count.
 */
export const calculateTotalEnvelopeEditCount = (
  dirtyFields: FieldNamesMarkedBoolean<Record<string, any[]>>,
  values?: Record<string, any[]>,
): number => {
  return Object.keys(dirtyFields).reduce((total, key) => {
    if (!isNil(values) && !isNil(values[key])) {
      if (key === envelope_type_enum.DOOR || key === envelope_type_enum.WINDOW) {
        extraFields = extraFields.filter((item) => item !== 'u_value');
      } else {
        extraFields.push('u_value');
      }

      const editedFields = dirtyFields[key].flatMap((obj, index) =>
        isNil(obj)
          ? []
          : Object.keys(obj).filter(
              (nestedKey) =>
                !extraFields.includes(nestedKey) &&
                obj[nestedKey] !== values[key][index][nestedKey] &&
                values[key][index][nestedKey] !== '',
            ),
      );

      total += editedFields.length;
    }
    return total;
  }, 0);
};

/**
 * Get the validation for the postal code based on the country.
 *
 * @example
 * ```ts
 * yup.object({
 *  countryId: yup.mixed<country_enum>().oneOf(Object.values(country_enum), REQUIRED_MESSAGE).required(REQUIRED_MESSAGE),
 *  postalCode: yup.string().when('countryId', getPostalCodeValidation()),
 * })
 */
/**
 * Per-country postal-code validation rule.
 *
 * - `numeric`: fixed-length, digits-only (e.g. DE "10115"). Validated by length + positivity.
 * - `regex`: alphanumeric formats (e.g. UK "BT4 1GW") that can't be expressed as a digit count.
 */
type PostalCodeRule = { type: 'numeric'; digits: number } | { type: 'regex'; regex: RegExp; example: string };

const POSTAL_CODE_RULES: Record<country_enum, PostalCodeRule> = {
  [country_enum.AT]: { type: 'numeric', digits: 4 },
  [country_enum.BE]: { type: 'numeric', digits: 4 },
  [country_enum.DE]: { type: 'numeric', digits: 5 },
  [country_enum.PL]: { type: 'numeric', digits: 5 },
  [country_enum.FR]: { type: 'numeric', digits: 5 },
  [country_enum.NL]: {
    type: 'regex',
    regex: /^[1-9]\d{3}\s?[A-Z]{2}$/i,
    example: '1012 AB',
  },
  // UK postcodes are alphanumeric and variable-length (e.g. "M1 1AE", "BT4 1GW", "SW1A 1AA").
  // The space before the final three characters is optional on input.
  [country_enum.UK]: {
    type: 'regex',
    regex: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    example: 'BT4 1GW',
  },
  // Czech Republic postcodes are numeric, 5 digits, and usually written with a space after the first three digits.
  [country_enum.CZ]: { type: 'numeric', digits: 5 },
};

/**
 * Whether the country's postal codes are alphanumeric (e.g. UK "BT4 1GW") rather than
 * digits-only (e.g. DE "10115"). Drives which input component and sanitizer to use so
 * letters aren't stripped from the field.
 *
 * Returns `false` for an unknown/undefined country (e.g. a form field rendered before the
 * country is selected), so callers safely fall back to the numeric behaviour.
 */
export const isAlphanumericPostalCode = (countryId: country_enum | null | undefined): boolean =>
  !!countryId && POSTAL_CODE_RULES[countryId]?.type === 'regex';

/**
 * Number of digits in a country's numeric postal code (e.g. DE = 5, AT = 4), used to extract a
 * manually typed postal code from free text via a `\d{n}` match.
 *
 * Alphanumeric (regex) countries — e.g. UK "BT4 1GW" — and unknown/undefined countries have no
 * fixed digit count, so this returns `0`. Callers doing digit-based extraction should gate on
 * `isAlphanumericPostalCode` for those.
 */
export const getPostalCodeLength = (countryId: country_enum | null | undefined): number => {
  const rule = countryId ? POSTAL_CODE_RULES[countryId] : undefined;
  return rule?.type === 'numeric' ? rule.digits : 0;
};

export const getPostalCodeValidation = (countryId: country_enum) => {
  const rule = POSTAL_CODE_RULES[countryId];

  if (rule.type === 'regex') {
    return yup
      .string()
      .test(
        'format',
        t('General_PostalCodeFormatMessage', { example: rule.example }),
        (postalCode) => typeof postalCode === 'string' && rule.regex.test(postalCode.trim()),
      );
  }

  return yup.string().test(
    'length',
    t('General_PostalCodeMessage', {
      min: '0'.repeat(rule.digits - 1) + '1',
      max: '9'.repeat(rule.digits),
    }),
    (postalCode) => typeof postalCode === 'string' && postalCode.length === rule.digits && parseInt(postalCode) > 0,
  );
};

export const getEmptyValue = <T>(value?: T | null | undefined) => value ?? ('' as T);

/**
 * Gives the args that are required to append to a array field
 *
 * @example
 * ```ts
 * const { append } = useFieldArray({ ... })
 * append(appendArgs(schema, 'path-to-the-field'))
 * ```
 */
export const appendArgs = (schema: yup.AnySchema, path: string = '') => {
  return yup.reach(schema, path + '[0]').cast({});
};
